import { useEffect, useMemo, useRef, useState } from 'react';

import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';

import { refLabel, useOpenRef, useSitePhotos, type ExplorePhoto } from './exploreApi';
import { LegacyRecordModal } from './LegacyArchivePage';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 사진 타임라인(DOC-136 · 사용자 결정 2026-10-28) — 그 현장의 사진 전부(옛 기록 · 일지 · 아차사고/의견)를 최근 것부터
 * 달 › 날로 묶는다. 끝까지 내리면 다음 쪽을 더 받는다. 한 장을 누르면 크게 보고(이전/다음), 그 기록을 연다.
 */
export function SitePhotoTimelinePage({ siteId }: { siteId: string }) {
  const photos = useSitePhotos(siteId);
  const items = useMemo(() => (photos.data?.pages ?? []).flatMap((p) => p.items), [photos.data]);
  const groups = useMemo(() => group(items), [items]);
  const [at, setAt] = useState<number | null>(null);
  const { open, peek, closePeek } = useOpenRef();
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = photos;

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    }, { rootMargin: '600px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const index = useMemo(() => new Map(items.map((p, i) => [p.id, i])), [items]);
  const current = at === null ? null : (items[at] ?? null);

  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <PageHeader title="사진 타임라인" />
      {photos.isPending ? (
        <LoadingBlock />
      ) : photos.isError ? (
        <LoadingBlock />
      ) : items.length === 0 ? (
        <EmptyCard>사진이 없습니다.</EmptyCard>
      ) : (
        <div className="space-y-6">
          {groups.map((month) => (
            <section key={month.key} className="space-y-4">
              <h2 className="pt-month">{month.label}</h2>
              {month.days.map((day) => (
                <div key={day.key}>
                  <div className="pt-day">
                    {day.label} <span>{day.items.length}</span>
                  </div>
                  <div className="pt-grid">
                    {day.items.map((p) => (
                      <button key={p.id} type="button" className="pt-cell" onClick={() => setAt(index.get(p.id) ?? null)}>
                        {p.url ? <img src={p.url} alt={p.caption ?? ''} loading="lazy" /> : <div className="pt-nophoto">불러오는 중입니다</div>}
                        <div className="pt-meta">
                          <b>{refLabel(p)}</b>
                          {p.caption && <span>{p.caption}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
          <div ref={sentinel} />
          {isFetchingNextPage && <LoadingBlock />}
        </div>
      )}

      {current && at !== null && (
        <Lightbox
          photo={current}
          hasPrev={at > 0}
          hasNext={at < items.length - 1 || !!hasNextPage}
          onPrev={() => setAt(Math.max(0, at - 1))}
          onNext={() => {
            if (at < items.length - 1) setAt(at + 1);
            else if (hasNextPage && !isFetchingNextPage) void fetchNextPage().then(() => setAt(at + 1));
          }}
          onOpen={() => {
            setAt(null);
            open(current, siteId);
          }}
          onClose={() => setAt(null)}
        />
      )}
      {peek && <LegacyRecordModal source={peek.source} code={peek.code} onClose={closePeek} />}
    </div>
  );
}

function Lightbox({
  photo,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onOpen,
  onClose,
}: {
  photo: ExplorePhoto;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onOpen: () => void;
  onClose: () => void;
}) {
  // 손잡이는 매번 새로 오므로 ref 로 최신 것을 읽는다 — 키 듣기를 다시 걸지 않으려고.
  const keys = useRef({ hasPrev, hasNext, onPrev, onNext, onClose });
  useEffect(() => {
    keys.current = { hasPrev, hasNext, onPrev, onNext, onClose };
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = keys.current;
      if (e.key === 'Escape') k.onClose();
      else if (e.key === 'ArrowLeft' && k.hasPrev) k.onPrev();
      else if (e.key === 'ArrowRight' && k.hasNext) k.onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="pt-box" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pt-box-head" onClick={(e) => e.stopPropagation()}>
        <span>
          <b>{refLabel(photo)}</b> · {photo.day}
          {photo.caption ? ` · ${photo.caption}` : ''}
        </span>
        <span className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onOpen}>
            기록 열기
          </button>
          <button type="button" className="btn-secondary" onClick={onClose} aria-label="닫기">
            ✕
          </button>
        </span>
      </div>
      <div className="pt-box-body">
        <button type="button" className="pt-nav" disabled={!hasPrev} onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="이전">
          ‹
        </button>
        {photo.url ? (
          <img src={photo.url} alt={photo.caption ?? ''} onClick={(e) => e.stopPropagation()} />
        ) : (
          <div className="pt-nophoto pt-nophoto-lg" onClick={(e) => e.stopPropagation()}>
            불러오는 중입니다
          </div>
        )}
        <button type="button" className="pt-nav" disabled={!hasNext} onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="다음">
          ›
        </button>
      </div>
    </div>
  );
}

type DayGroup = { key: string; label: string; items: ExplorePhoto[] };
type MonthGroup = { key: string; label: string; days: DayGroup[] };

/** 받은 차례(최근 것부터) 그대로 달 › 날로 자른다. */
function group(items: ExplorePhoto[]): MonthGroup[] {
  const months: MonthGroup[] = [];
  for (const p of items) {
    const [y, m, d] = p.day.split('-');
    const mk = `${y}-${m}`;
    let month = months[months.length - 1];
    if (!month || month.key !== mk) {
      month = { key: mk, label: `${y}년 ${Number(m)}월`, days: [] };
      months.push(month);
    }
    let day = month.days[month.days.length - 1];
    if (!day || day.key !== p.day) {
      const w = WEEKDAYS[new Date(Number(y), Number(m) - 1, Number(d)).getDay()];
      day = { key: p.day, label: `${Number(m)}월 ${Number(d)}일 (${w})`, items: [] };
      month.days.push(day);
    }
    day.items.push(p);
  }
  return months;
}

const STYLES = `
.pt-month { font-size: 16px; font-weight: 700; margin: 0; padding-bottom: 6px; border-bottom: 1px solid var(--border); }
.pt-day { font-size: 13px; font-weight: 600; margin: 0 0 8px; color: var(--text-secondary); }
.pt-day span { color: var(--text-muted); font-weight: 500; }
.pt-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(168px, 1fr)); gap: 10px; }
.pt-cell { display: flex; flex-direction: column; padding: 0; border: 1px solid var(--border); border-radius: 10px; background: var(--bg-card, #fff); overflow: hidden; cursor: pointer; text-align: left; }
.pt-cell:hover { box-shadow: 0 2px 8px rgba(0,0,0,.08); }
.pt-cell img { width: 100%; height: 132px; object-fit: cover; display: block; }
.pt-nophoto { height: 132px; display: flex; align-items: center; justify-content: center; background: #f8fafc; font-size: 12px; color: var(--text-muted); }
.pt-meta { display: flex; flex-direction: column; gap: 2px; padding: 6px 8px; font-size: 12px; min-width: 0; }
.pt-meta b { font-weight: 600; }
.pt-meta span { color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pt-box { position: fixed; inset: 0; z-index: 1000; background: rgba(15,23,42,.88); display: flex; flex-direction: column; }
.pt-box-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 16px; color: #fff; font-size: 14px; }
.pt-box-body { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; gap: 12px; padding: 0 12px 16px; }
.pt-box-body img { max-width: calc(100% - 120px); max-height: 100%; object-fit: contain; border-radius: 6px; }
.pt-nophoto-lg { width: 480px; max-width: calc(100% - 120px); height: 320px; border-radius: 6px; }
.pt-nav { width: 48px; height: 48px; flex: none; border: none; border-radius: 50%; background: rgba(255,255,255,.14); color: #fff; font-size: 28px; line-height: 1; cursor: pointer; }
.pt-nav:disabled { opacity: .3; cursor: default; }
`;
